import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle2, XCircle, Clock, MinusCircle } from 'lucide-react';
import type { CircuitWithSteps } from '../../services/signature/signatureService';
import { noteTexts, parseRefuseAnnotations } from '../../services/signature/refuseAnnotations';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { CircuitStatusBadge } from './circuitStatusBadge';

import { formatAppDate } from '../../lib/dateFormat';
interface Props {
  circuit: CircuitWithSteps;
}

function StepIcon({ status }: { status: string }) {
  if (status === 'signed') return <CheckCircle2 className="w-4 h-4 text-green-600" />;
  if (status === 'rejected') return <XCircle className="w-4 h-4 text-red-600" />;
  if (status === 'cancelled') return <MinusCircle className="w-4 h-4 text-neutral-400" />;
  return <Clock className="w-4 h-4 text-amber-600" />;
}

export function CircuitTimeline({ circuit }: Props) {
  const steps = [...circuit.steps].sort((a, b) => a.step_order - b.step_order);
  const pdfNotes = noteTexts(
    parseRefuseAnnotations((circuit as { refuse_annotations?: unknown }).refuse_annotations),
  );
  return (
    <ol className="space-y-2">
      {steps.map((s) => (
        <li key={s.id} className="flex items-start gap-3 text-sm">
          <div className="mt-0.5 flex-shrink-0">
            <StepIcon status={s.status} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {s.collaborator?.id ? (
                <CollaboratorLink collaboratorId={s.collaborator.id} title={`Voir le profil de ${s.collaborator.full_name}`}>
                  <span className="font-medium text-cime-text-primary">
                    {s.collaborator.full_name ?? '—'}
                  </span>
                </CollaboratorLink>
              ) : (
                <span className="font-medium text-cime-text-primary">
                  {s.collaborator?.full_name ?? '—'}
                </span>
              )}
              <CircuitStatusBadge status={s.status as 'pending' | 'signed' | 'rejected' | 'cancelled'} size="sm" kind={circuit.kind} />
              {s.deadline_at && s.status === 'pending' && (
                <span className="text-xs text-amber-700">
                  Échéance : {formatAppDate(s.deadline_at)}
                </span>
              )}
            </div>
            {s.signed_at && (
              <div className="text-xs text-cime-text-muted mt-0.5">
                {format(new Date(s.signed_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                {typeof s.signer_ip === 'string' && ` · IP ${s.signer_ip}`}
              </div>
            )}
            {s.comment && (
              <div className="mt-1 text-xs italic text-cime-text-muted">Motif : « {s.comment} »</div>
            )}
            {s.status === 'rejected' && pdfNotes.map((t) => (
              <div key={t} className="mt-1 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 whitespace-pre-wrap">
                Note PDF : {t}
              </div>
            ))}
          </div>
        </li>
      ))}
    </ol>
  );
}
